import React, { useState, useEffect } from 'react';
import { TextField, Typography, Tooltip, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PropTypes from 'prop-types';

/**
 * ServingSize Component
 * A reusable component for capturing min and max serving sizes for food items
 *
 * @param {Object} openFood - The food item object containing value and food properties
 * @param {Function} servesMinOnChange - Handler for min serving size changes
 * @param {Function} servesMaxOnChange - Handler for max serving size changes
 * @param {boolean} disabled - Optional disabled state override
 * @param {boolean} showBorder - Optional flag to show/hide border (default: true)
 * @param {Function} onValidationChange - Optional callback for validation state changes
 * @param {Object} sx - Optional additional styling
 */
function ServingSize({
    serves,
    servesMinOnChange,
    servesMaxOnChange,
    disabled = false,
    showBorder = true,
    sx = {},
}) {
    // Use passed disabled prop or check if UOM is 'people'
    //const isDisabled = disabled || uom === 'people';

    // State for validation errors
    const [minError, setMinError] = useState('');
    const [maxError, setMaxError] = useState('');

    // Get current values - don't use || '' to allow clearing
    const currentMin = serves?.min ?? '';
    const currentMax = serves?.max ?? '';

    // Validation function
    const validateServes = (min, max) => {
        let minErr = '';
        let maxErr = '';
        let isValid = true;

        // Allow empty values (optional field)
        if (min === '' && max === '') {
            setMinError('');
            setMaxError('');
            return true;
        }

        // Convert to numbers for validation
        const minNum = min ? parseInt(min) : 0;
        const maxNum = max ? parseInt(max) : 0;

        if (min === '' && max !== '') {
            minErr = 'Min value cannot be empty if Max is set';
            isValid = false;
        }

        if (min !== '' && max === '') {
            minErr = 'Max value cannot be empty if Min is set';
            isValid = false;
        }

        // Check if values are valid numbers and >= 1 (only if not empty)
        if (min !== '' && min !== null && min !== undefined) {
            if (!minNum || minNum < 1) {
                minErr = 'Min must be at least 1';
                isValid = false;
            }
        }

        if (max !== '' && max !== null && max !== undefined) {
            if (!maxNum || maxNum < 1) {
                maxErr = 'Max must be at least 1';
                isValid = false;
            }
        }

        // Check if min <= max (only when both values are present and valid)
        if (
            min !== '' &&
            max !== '' &&
            minNum > 0 &&
            maxNum > 0 &&
            minNum > maxNum
        ) {
            minErr = 'Min cannot be greater than Max';
            isValid = false;
        }

        setMinError(minErr);
        setMaxError(maxErr);

        return isValid;
    };

    // Validate on mount and when values change
    useEffect(() => {
        if (!disabled) {
            validateServes(currentMin, currentMax);
        }
    }, [currentMin, currentMax, disabled]);

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'start',
                gap: 1,
                border: showBorder ? '1px solid #e0e0e0' : 'none',
                borderRadius: showBorder ? '4px' : 0,
                p: showBorder ? 2 : 0,
                backgroundColor:
                    showBorder && disabled ? '#f5f5f5' : 'transparent',
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: '120px',
                    gap: 0.5,
                    m: 0,
                    mt: 0.75,
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 'medium',
                        color: disabled ? '#9e9e9e' : 'inherit',
                    }}
                >
                    Serving Size:
                </Typography>
                <Tooltip
                    title="If you don't need a range (e.g., serves 6-8), put the same value in both Min and Max"
                    placement="top"
                    arrow
                >
                    <InfoOutlinedIcon
                        sx={{
                            fontSize: 16,
                            color: disabled ? '#9e9e9e' : '#757575',
                            cursor: 'pointer',
                        }}
                    />
                </Tooltip>
            </Box>
            <TextField
                id="servesMin"
                label="Min (people)"
                type="number"
                value={currentMin}
                onChange={servesMinOnChange}
                variant="outlined"
                size="small"
                margin="dense"
                disabled={disabled}
                error={!!minError}
                helperText={minError}
                sx={{ width: '150px', mt: 0, mb: 0 }}
                InputProps={{
                    inputProps: { min: 1 },
                }}
            />
            <TextField
                id="servesMax"
                label="Max (people)"
                type="number"
                value={currentMax}
                onChange={servesMaxOnChange}
                variant="outlined"
                size="small"
                margin="dense"
                disabled={disabled}
                error={!!maxError}
                helperText={maxError}
                sx={{ width: '150px', mt: 0, mb: 0 }}
                InputProps={{
                    inputProps: { min: 1 },
                }}
            />
        </Box>
    );
}

ServingSize.propTypes = {
    serves: PropTypes.object.isRequired,
    servesMinOnChange: PropTypes.func,
    servesMaxOnChange: PropTypes.func,
    disabled: PropTypes.bool,
    showBorder: PropTypes.bool,
    sx: PropTypes.object,
};

export default ServingSize;
